import type { ApolloError } from '@apollo/client';
import { useMemo } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import { logError } from '@zg-rentals/log-error';
import { isHotpads, isZillow } from '@zg-rentals/multibrand-support';
import apolloClient from '../lib/apollo';
import { AuthCheckWrapper_GetHotPadsUserDocument, AuthCheckWrapper_GetZillowUserDocument } from '../types/GraphqlTypes';
import type { AuthCheckWrapper_GetHotPadsUserQuery, AuthCheckWrapper_GetZillowUserQuery } from '../types/GraphqlTypes';

/**
 * Formats raw user data from GraphQL queries into the expected ZRM user
 */
const formatRawUser = (
  rawUser:
    | NonNullable<AuthCheckWrapper_GetHotPadsUserQuery['RH_getHotpadsUser']>
    | NonNullable<AuthCheckWrapper_GetZillowUserQuery['RH_getZillowUser']>,
) => {
  return {
    ...rawUser,
    isSingleFamilyEnterpriseCustomer: rawUser.isSingleFamilyEnterpriseCustomer ?? undefined,
    summary: rawUser.summary
      ? {
          activeListingCount: rawUser.summary.activeListingCount ?? undefined,
          totalListingCount: rawUser.summary.totalListingCount ?? undefined,
        }
      : undefined,
    restrictions: rawUser.restrictions
      ? {
          ...rawUser.restrictions,
          ctaText: rawUser.restrictions.ctaText ?? '',
          ctaUrl: rawUser.restrictions.ctaUrl ?? '',
          isAllowed: !!rawUser.restrictions.isAllowed,
          messageCode: rawUser.restrictions.messageCode ?? '',
          title: rawUser.restrictions.title ?? '',
          partialMessages:
            rawUser.restrictions.partialMessages
              ?.filter((msg): msg is NonNullable<typeof msg> => msg !== null)
              .map((msg) => ({
                ...msg,
                isBold: msg.isBold ?? false,
                text: msg.text ?? '',
                url: msg.url ?? '',
                className: msg.className ?? '',
              })) ?? [],
        }
      : undefined,
  };
};

export default (brandName: string | null) => {
  const isZillowBrand = isZillow(brandName);
  const isHotPadsBrand = isHotpads(brandName);

  const { data, isLoading, error } = useQuery<
    AuthCheckWrapper_GetHotPadsUserQuery | AuthCheckWrapper_GetZillowUserQuery,
    ApolloError | Error
  >({
    queryKey: ['userData', brandName],
    queryFn: async () => {
      if (isHotPadsBrand) {
        const result = await apolloClient.query<AuthCheckWrapper_GetHotPadsUserQuery>({
          query: AuthCheckWrapper_GetHotPadsUserDocument,
          fetchPolicy: 'cache-first',
        });
        return result.data;
      } else if (isZillowBrand) {
        const result = await apolloClient.query<AuthCheckWrapper_GetZillowUserQuery>({
          query: AuthCheckWrapper_GetZillowUserDocument,
          fetchPolicy: 'cache-first',
        });
        return result.data;
      }
      const error = new Error('Unsupported brand');
      logError({ error, context: { brand: brandName, info: 'Error fetching user - Unsupported brand' } });
      throw error;
    },
    enabled: !!brandName,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: 1,
    meta: {
      context: { brand: brandName, info: 'Fetching user data' },
    },
  });

  const user = useMemo(() => {
    if (!data) return undefined;

    const rawUser =
      ('RH_getZillowUser' in data ? data.RH_getZillowUser : null) ||
      ('RH_getHotpadsUser' in data ? data.RH_getHotpadsUser : null) ||
      undefined;

    if (!rawUser) return undefined;

    return formatRawUser(rawUser);
  }, [data]);

  return {
    user,
    isLoading,
    error: error as ApolloError | Error | undefined,
  };
};
